import { createContext, useContext, useEffect, useState } from "react";
import { api, loginWithGoogle as loginApi, fetchMe } from "../lib/api";
import { isTauri, onAuthToken } from "../lib/native";

const AuthContext = createContext(null);

const TOKEN_KEY = "listener_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        async function bootstrap() {
            if (!token) {
                setLoading(false);
                return;
            }
            try {
                const me = await fetchMe();
                if (!cancelled) setUser(me);
            } catch {
                if (!cancelled) {
                    localStorage.removeItem(TOKEN_KEY);
                    setToken(null);
                    setUser(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }
        bootstrap();
        return () => {
            cancelled = true;
        };
    }, [token]);

    // Desktop: catch the JWT handed back from the system-browser sign-in.
    useEffect(() => {
        if (!isTauri()) return;
        let unlisten = () => {};
        onAuthToken((jwt) => { loginWithToken(jwt); }).then((fn) => { unlisten = fn || (() => {}); });
        return () => { try { unlisten(); } catch { /* ignore */ } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function loginWithGoogleCredential(credential) {
        const { token: jwt, user: u } = await loginApi(credential);
        localStorage.setItem(TOKEN_KEY, jwt);
        setToken(jwt);
        setUser(u);
        return u;
    }

    // Desktop: a JWT handed back from the system-browser sign-in via the
    // lisn:// deep link. Trust it, persist it, and resolve the user.
    async function loginWithToken(jwt) {
        if (!jwt) return null;
        localStorage.setItem(TOKEN_KEY, jwt);
        setToken(jwt);
        try {
            const me = await fetchMe();
            setUser(me);
            return me;
        } catch {
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setUser(null);
            return null;
        }
    }

    function logout() {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
        delete api.defaults.headers.common.Authorization;
    }

    return (
        <AuthContext.Provider
            value={{ user, token, loading, loginWithGoogleCredential, loginWithToken, logout }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export { TOKEN_KEY };
